import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TIME_FORMAT } from '@/app/constants/date'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { determineRouteType } from '@/app/common/tmsRouteTypeHelper'
import { toast } from '@/lib/toasts/api'
import TmsLoadsEditor from '@/modules/drivers/shared/tms/TmsLoadsEditor'
import { useTmsLoadsEditor } from '@/modules/drivers/shared/tms/useTmsLoadsEditor'
import { useGlobalDateRange } from './useGlobalDateRange'

const SYNTHETIC_PREFIX = '_tms_'

interface BusinessTripEditTmsViewProps {
  tripData: any
  companyId: string
  driverId: string
  onCancel: () => void
  onSave: (events: EventModel[]) => void
}

const BusinessTripEditTmsView: React.FC<BusinessTripEditTmsViewProps> = ({
  tripData,
  companyId,
  driverId,
  onCancel,
  onSave,
}) => {
  const { t } = useLocalize()
  const globalDateRange = useGlobalDateRange()

  const editor = useTmsLoadsEditor({
    companyId,
    driverId,
    initialDateFrom: globalDateRange.from,
    initialDateTo: globalDateRange.to,
  })

  const handleSave = async () => {
    if (!editor.validateSelection()) return
    if (!(await editor.persistChanges())) return

    // WPF TripViewModel.ShowTmsData: brak zaznaczonych zamyka okno bez zmian w delegacji.
    const selectedItems = editor.selectedItems
    if (selectedItems.length === 0) {
      onCancel()
      return
    }

    // WPF AddItemBasedOnTmsData: pomija zdarzenie, gdy delegacja ma już
    // zdarzenie z tym samym numerem dokumentu i datą. Numer syntetyczny
    // liczy się tu jak brak numeru.
    const existingEvents: EventModel[] = tripData?.events || []
    const isSynthetic = (docNumber?: string | null) => !!docNumber?.startsWith(SYNTHETIC_PREFIX)
    const takenKeys = new Set<string>(
      existingEvents.map(
        (e) =>
          `${isSynthetic(e.docNumber) ? '' : (e.docNumber ?? '')}|${e.date ? moment(e.date).format(TIME_FORMAT) : ''}`,
      ),
    )
    // Kolejne numery syntetyczne za tymi, które delegacja już ma - inaczej
    // parowanie załadunek/rozładunek po numerze trafi na cudzą parę.
    let nextSynthetic =
      Math.max(
        -1,
        ...existingEvents
          .filter((e) => isSynthetic(e.docNumber))
          .map((e) => Number((e.docNumber as string).slice(SYNTHETIC_PREFIX.length)) || 0),
      ) + 1
    const events: EventModel[] = []
    const addEvent = (
      tripEvent: TripEventType,
      docNumber: string | undefined,
      pairingDocNumber: string,
      date: Moment,
      country: any,
      secondDate: Moment,
      secondCountry: any,
      routeType: EventModel['tmsRouteType'],
      lp: number,
    ) => {
      const key = `${docNumber ?? ''}|${date.format(TIME_FORMAT)}`
      if (takenKeys.has(key)) return
      takenKeys.add(key)
      events.push({
        date,
        tripEvent,
        country: country || null,
        lp,
        docNumber: pairingDocNumber,
        secondDate,
        secondCountry: secondCountry || null,
        tmsRouteType: routeType,
      })
    }

    selectedItems.forEach((item, i) => {
      const routeType = determineRouteType(item.loadingCountry, item.unloadingCountry, true)
      // Syntetyczny numer, żeby przy odczycie delegacji sparować załadunek z rozładunkiem.
      const pairingDocNumber = item.docNumber || `${SYNTHETIC_PREFIX}${nextSynthetic++}`
      const loadingDate = moment(item.loadingDate)
      const unloadingDate = moment(item.unloadingDate)

      addEvent(TripEventType.Loading, item.docNumber, pairingDocNumber, loadingDate,
        item.loadingCountry, unloadingDate, item.unloadingCountry, routeType, i)
      addEvent(TripEventType.Unloading, item.docNumber, pairingDocNumber, unloadingDate,
        item.unloadingCountry, loadingDate, item.loadingCountry, routeType, i)
    })

    if (events.length > 0) {
      onSave(events)
      toast.success({ text: t('newDelegation.tms-events-created') })
    } else {
      onCancel()
    }
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-base-50">
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-bg-white tw-px-6 tw-py-3">
        <span className="tw-text-sm tw-font-medium tw-text-content-base">
          {t('tmsData')}
        </span>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90"
          >
            {t('common.save')}
          </button>
        </div>
      </div>

      <TmsLoadsEditor editor={editor} companyId={companyId} />
    </div>
  )
}

export default BusinessTripEditTmsView
